// client/src/components/watchdog/WatchdogSetupTab.jsx
// Configuration form for the Watchdog pipeline — credentials, location, pipeline settings.
// Shares the same config table as Command for credentials/location.

import React, { useState, useEffect } from "react";
import { useConfig, useLocations } from "../../hooks/useCommand";
import { useWatchdogConfig } from "../../hooks/useWatchdog";

const s = {
    container: { maxWidth: "720px", margin: "0 auto" },
    section: {
        background: "linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%)",
        borderRadius: "12px",
        padding: "24px",
        border: "1px solid rgba(139, 92, 246, 0.1)",
        marginBottom: "16px",
    },
    sectionTitle: { color: "#e2e8f0", fontSize: "16px", fontWeight: 700, marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" },
    label: { color: "#94a3b8", fontSize: "12px", fontWeight: 600, marginBottom: "4px", display: "block" },
    input: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", boxSizing: "border-box",
    },
    textarea: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", minHeight: "80px", resize: "vertical", boxSizing: "border-box",
        fontFamily: "inherit",
    },
    select: {
        width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "13px", outline: "none", boxSizing: "border-box",
    },
    btn: (primary) => ({
        padding: "10px 24px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "14px",
        background: primary ? "linear-gradient(135deg, #8b5cf6, #6366f1)" : "rgba(255,255,255,0.06)",
        color: primary ? "#fff" : "#94a3b8",
        opacity: primary ? 1 : 0.8,
    }),
    btnRow: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" },
    status: (ok) => ({
        background: ok ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
        color: ok ? "#10b981" : "#ef4444",
        padding: "10px 16px", borderRadius: "8px", fontSize: "13px", marginTop: "12px",
    }),
    toggle: (on) => ({
        width: 42, height: 22, borderRadius: 11,
        background: on ? "#22c55e" : "#334155",
        cursor: "pointer", position: "relative",
        transition: "background 0.2s", border: "none", flexShrink: 0,
        display: "inline-block", verticalAlign: "middle",
    }),
    toggleDot: (on) => ({
        width: 16, height: 16, borderRadius: 8,
        background: "#fff", position: "absolute",
        top: 3, left: on ? 23 : 3, transition: "left 0.2s",
    }),
    modeTag: (mode) => ({
        display: "inline-block", padding: "4px 12px", borderRadius: "6px",
        fontSize: "12px", fontWeight: 700, marginLeft: "12px",
        background: mode === "auto_execute" ? "#ef444420" : "#06b6d420",
        color: mode === "auto_execute" ? "#ef4444" : "#06b6d4",
        letterSpacing: "0.5px",
    }),
};

export default function WatchdogSetupTab() {
    // ── Shared credentials (same config table as Command) ──
    const { config, loading, saving, error, save } = useConfig();
    const { locations, loading: locLoading, fetchLocations } = useLocations();
    const [form, setForm] = useState({});
    const [saved, setSaved] = useState(false);

    // ── Watchdog-specific settings ──
    const { config: wdConfig, saving: wdSaving, save: wdSave } = useWatchdogConfig();
    const [wdForm, setWdForm] = useState({
        watchdog_enabled: true,
        watchdog_mode: "monitor_only",
        watchdog_debounce_ms: 5000,
        watchdog_concurrency: 4,
    });

    useEffect(() => {
        if (config) setForm({ ...config });
    }, [config]);

    useEffect(() => {
        if (wdConfig) setWdForm({ ...wdConfig });
    }, [wdConfig]);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
    const setWd = (key, val) => setWdForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        // Save shared config
        const ok1 = await save(form);
        // Save watchdog-specific config
        await wdSave(wdForm);
        if (ok1) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    };

    if (loading) return <div style={{ color: "#94a3b8", padding: "40px", textAlign: "center" }}>Loading configuration…</div>;

    return (
        <div style={s.container}>
            {/* ShipHero Credentials */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🔑 ShipHero Credentials</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>API Token</label>
                        <input style={s.input} type="password" value={form.shiphero_token || ""} onChange={e => set("shiphero_token", e.target.value)} placeholder="Bearer token" />
                    </div>
                    <div>
                        <label style={s.label}>Account ID</label>
                        <input style={s.input} value={form.account_id || ""} onChange={e => set("account_id", e.target.value)} placeholder="Optional" />
                    </div>
                </div>
                <div>
                    <label style={s.label}>Warehouse ID</label>
                    <input style={s.input} value={form.warehouse_id || ""} onChange={e => set("warehouse_id", e.target.value)} placeholder="Filter orders by warehouse" />
                </div>
            </div>

            {/* Shopify Credentials */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🏪 Shopify Credentials</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Store URL</label>
                        <input style={s.input} value={form.shopify_store || ""} onChange={e => set("shopify_store", e.target.value)} placeholder="your-store.myshopify.com" />
                    </div>
                    <div>
                        <label style={s.label}>Access Token</label>
                        <input style={s.input} type="password" value={form.shopify_token || ""} onChange={e => set("shopify_token", e.target.value)} placeholder="shpat_xxxxx" />
                    </div>
                </div>
            </div>

            {/* Location Settings */}
            <div style={s.section}>
                <div style={s.sectionTitle}>📍 Location Settings</div>
                <div style={{ marginBottom: "12px" }}>
                    <button style={s.btn(false)} onClick={fetchLocations} disabled={locLoading}>
                        {locLoading ? "Fetching…" : "🔄 Fetch Shopify Locations"}
                    </button>
                </div>
                {locations.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                        <label style={s.label}>Select Expected Location</label>
                        <select style={s.select} value={form.expected_location_id || ""} onChange={e => {
                            const loc = locations.find(l => l.id === e.target.value);
                            set("expected_location_id", e.target.value);
                            if (loc) set("expected_location_name", loc.name);
                        }}>
                            <option value="">— Select —</option>
                            {locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.id})</option>)}
                        </select>
                    </div>
                )}
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Expected Location ID</label>
                        <input style={s.input} value={form.expected_location_id || ""} onChange={e => set("expected_location_id", e.target.value)} placeholder="Enter or select above" />
                    </div>
                    <div>
                        <label style={s.label}>Location Name</label>
                        <input style={s.input} value={form.expected_location_name || ""} onChange={e => set("expected_location_name", e.target.value)} placeholder="Descriptive name" />
                    </div>
                </div>
            </div>

            {/* Tags */}
            <div style={s.section}>
                <div style={s.sectionTitle}>🏷️ Tags</div>
                <div style={s.row}>
                    <div>
                        <label style={s.label}>Cancel Tag</label>
                        <input style={s.input} value={form.cancel_tag || ""} onChange={e => set("cancel_tag", e.target.value)} placeholder="SH-LOCATION-MISMATCH" />
                    </div>
                    <div>
                        <label style={s.label}>Additional Tags (one per line)</label>
                        <textarea style={s.textarea} value={form.additional_tags || ""} onChange={e => set("additional_tags", e.target.value)} placeholder="One tag per line" />
                    </div>
                </div>
            </div>

            {/* Watchdog Pipeline Settings */}
            <div style={s.section}>
                <div style={s.sectionTitle}>
                    🐕 Watchdog Pipeline
                    <span style={s.modeTag(wdForm.watchdog_mode)}>
                        {wdForm.watchdog_mode === "auto_execute" ? "AUTO EXECUTE" : "MONITOR ONLY"}
                    </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "16px", flexWrap: "wrap" }}>
                    <div>
                        <label style={s.label}>Enabled</label>
                        <button
                            style={s.toggle(wdForm.watchdog_enabled)}
                            onClick={() => setWd("watchdog_enabled", !wdForm.watchdog_enabled)}
                        >
                            <div style={s.toggleDot(wdForm.watchdog_enabled)} />
                        </button>
                    </div>

                    <div>
                        <label style={s.label}>Mode</label>
                        <select
                            style={{ ...s.select, width: "180px" }}
                            value={wdForm.watchdog_mode}
                            onChange={e => setWd("watchdog_mode", e.target.value)}
                        >
                            <option value="monitor_only">Monitor Only</option>
                            <option value="auto_execute">Auto Execute</option>
                        </select>
                    </div>
                </div>

                <div style={s.row}>
                    <div>
                        <label style={s.label}>Debounce (ms)</label>
                        <input
                            style={s.input}
                            type="number"
                            value={wdForm.watchdog_debounce_ms}
                            onChange={e => setWd("watchdog_debounce_ms", parseInt(e.target.value) || 5000)}
                        />
                        <span style={{ color: "#64748b", fontSize: "11px", marginTop: "4px", display: "block" }}>
                            Delay before validating an order after a webhook event
                        </span>
                    </div>
                    <div>
                        <label style={s.label}>Concurrency</label>
                        <input
                            style={s.input}
                            type="number"
                            min={1}
                            max={20}
                            value={wdForm.watchdog_concurrency}
                            onChange={e => setWd("watchdog_concurrency", parseInt(e.target.value) || 4)}
                        />
                        <span style={{ color: "#64748b", fontSize: "11px", marginTop: "4px", display: "block" }}>
                            Max orders validated in parallel per tick
                        </span>
                    </div>
                </div>
            </div>

            <div style={s.btnRow}>
                <button style={s.btn(true)} onClick={handleSave} disabled={saving || wdSaving}>
                    {saving || wdSaving ? "Saving…" : "💾 Save Configuration"}
                </button>
            </div>

            {saved && <div style={s.status(true)}>✓ Configuration saved successfully</div>}
            {error && <div style={s.status(false)}>✗ {error}</div>}
        </div>
    );
}
